import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLines } from "../components/Reveal";
import { BLOGS } from "../lib/blogs";

const formatDate = (d) =>
  new Date(d).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

export default function Blog() {
  const [featured, ...rest] = BLOGS;

  return (
    <div data-testid="blog-page" className="pt-28 md:pt-36 pb-24 md:pb-40">
      <section className="max-w-[1400px] mx-auto px-6 md:px-10">
        <p className="font-sans text-xs tracking-[0.4em] uppercase text-[#7D8C7A] mb-8">
          Journal
        </p>
        <h1 className="font-serif text-[#2C3C30] text-6xl md:text-8xl lg:text-[8vw] leading-[0.9] tracking-tighter">
          <MaskedLines
            lines={["Notes on", <em key="e" className="text-[#7D8C7A]">gut & health.</em>]}
          />
        </h1>
        <Reveal delay={0.4}>
          <p className="font-sans text-lg md:text-xl text-[#566158] max-w-2xl mt-10 leading-relaxed">
            Evidence-based perspectives on the topics shaping nutrition and
            healthcare right now — written to inform, not overwhelm.
          </p>
        </Reveal>
      </section>

      {/* Featured */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 mt-16 md:mt-24">
        <Reveal>
          <Link
            to={`/blog/${featured.slug}`}
            data-testid="blog-featured"
            className="group grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center"
          >
            <div className="md:col-span-7 overflow-hidden rounded-3xl">
              <motion.img
                src={featured.image}
                alt={featured.title}
                className="w-full h-[320px] md:h-[460px] object-cover"
                whileHover={{ scale: 1.04 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
            <div className="md:col-span-5">
              <span className="font-sans text-xs uppercase tracking-[0.25em] text-[#566158] border border-[#2C3C30]/20 rounded-full px-4 py-1.5">
                {featured.category}
              </span>
              <h2 className="font-serif text-4xl md:text-5xl text-[#2C3C30] tracking-tight mt-6 leading-[1.02]">
                {featured.title}
              </h2>
              <p className="font-sans text-[#566158] leading-relaxed mt-5">
                {featured.excerpt}
              </p>
              <div className="flex items-center gap-4 mt-6 font-sans text-sm text-[#7D8C7A]">
                <span>{formatDate(featured.date)}</span>
                <span>&middot;</span>
                <span>{featured.readTime}</span>
              </div>
              <span className="group inline-flex items-center gap-2 mt-8 font-sans text-[#2C3C30] uppercase tracking-wide text-sm border-b border-[#2C3C30]/40 pb-1">
                Read article
                <ArrowUpRight
                  size={16}
                  className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                />
              </span>
            </div>
          </Link>
        </Reveal>
      </section>

      {/* Grid */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 mt-20 md:mt-32">
        <div className="border-t border-[#2C3C30]/15 pt-12 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
          {rest.map((post, i) => (
            <Reveal key={post.slug} delay={(i % 3) * 0.1}>
              <Link
                to={`/blog/${post.slug}`}
                data-testid={`blog-card-${i}`}
                className="group flex flex-col h-full"
              >
                <div className="overflow-hidden rounded-2xl">
                  <motion.img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-[240px] object-cover"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  />
                </div>
                <span className="font-sans text-xs uppercase tracking-[0.25em] text-[#7D8C7A] mt-6">
                  {post.category}
                </span>
                <h3 className="font-serif text-3xl text-[#2C3C30] tracking-tight mt-3 leading-tight group-hover:text-[#7D8C7A] transition-colors duration-300">
                  {post.title}
                </h3>
                <p className="font-sans text-[#566158] leading-relaxed mt-3 flex-1">
                  {post.excerpt}
                </p>
                <div className="flex items-center gap-3 mt-5 font-sans text-sm text-[#7D8C7A]">
                  <span>{formatDate(post.date)}</span>
                  <span>&middot;</span>
                  <span>{post.readTime}</span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
